import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import {
  ActivateCategoryDocument,
  type ActivateCategoryMutation,
  type ActivateCategoryMutationVariables,
  DeactivateCategoryDocument,
  type DeactivateCategoryMutation,
  type DeactivateCategoryMutationVariables,
  GetCategoriesDocument,
} from "~/features/admin/categories/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/**
 * Activate / deactivate a category. The hook returns plain mutation submitters
 * and a `processing` flag; the calling container owns the confirm dialog.
 *
 * Reuses V1's typed documents during the read-only phase; will swap to V2-local
 * mutation docs in `gqls/` once `pnpm run codegen` produces them.
 */
export function useCategoryActivation() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);

  // An ancestor's activation state changes every descendant's effective schemas
  // (the resolver excludes deactivated ancestors), a dependency Apollo cannot trace.
  const [activate] = useMutation<ActivateCategoryMutation, ActivateCategoryMutationVariables>(
    ActivateCategoryDocument,
    {
      refetchQueries: [{ query: GetCategoriesDocument, variables: { tenantId } }],
      awaitRefetchQueries: true,
      update: (cache) => {
        cache.evict({ fieldName: "cnutListEffectiveCategoryPropertySchemas" });
        cache.gc();
      },
    },
  );

  const [deactivate] = useMutation<DeactivateCategoryMutation, DeactivateCategoryMutationVariables>(
    DeactivateCategoryDocument,
    {
      refetchQueries: [{ query: GetCategoriesDocument, variables: { tenantId } }],
      awaitRefetchQueries: true,
      update: (cache) => {
        cache.evict({ fieldName: "cnutListEffectiveCategoryPropertySchemas" });
        cache.gc();
      },
    },
  );

  const onActivate = useCallback(
    async ({ id }: { id: string }) => {
      setProcessing(true);
      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        await activate({ variables: { input: { tenantId, id } } });
        toast.success(t("feature.common.activatedSuccessfully", { model: "category" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [activate, t, tenantId],
  );

  const onDeactivate = useCallback(
    async ({ id }: { id: string }) => {
      setProcessing(true);
      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        const res = await deactivate({ variables: { input: { tenantId, id } } });
        const handled = match(res.data?.cnutDeactivateCategory)
          .with({ __typename: "ValidationError" }, (e) => {
            const message = e.fields?.[0]?.message ?? t("error.unknown");
            toast.error(message);
            return true;
          })
          .otherwise(() => false);
        if (handled) return;
        toast.success(t("feature.common.deactivatedSuccessfully", { model: "category" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [deactivate, t, tenantId],
  );

  return { onActivate, onDeactivate, processing };
}
